import { SENTIMENT_COMMENTS_PATH, SENTIMENT_SUMMARY_PATH } from '../sentiment/sentiment-paths';

export type TrackingProduct = 'sentiment' | 'corporate' | 'followup' | 'onsite' | 'online';

export interface TrackingContext {
  product: TrackingProduct;
  section: string;
  subsection?: string;
}

export type TrackingScope = 'topics' | 'areas' | 'dimensions';
export type TrackingListWidget = 'topic_trend' | 'breakdown' | 'property_ranking';
export type TrackingWidget =
  | TrackingListWidget
  | 'sentiment_index'
  | 'most_valued'
  | 'least_valued'
  | 'biggest_improvement'
  | 'mentions';
export type TrackingWidgetAction =
  | 'see_negatives'
  | 'open_mentions'
  | 'select_item'
  | 'sort'
  | 'see_all'
  | 'open_sentiment';
export type TrackingCommentSource = 'followup' | 'onsite' | 'online' | 'concierge';
export type TrackingOta =
  | 'tripadvisor'
  | 'booking'
  | 'expedia'
  | 'despegar'
  | 'google'
  | 'facebook'
  | 'expedia_group'
  | 'airbnb';
export type TrackingIntensity = 'high' | 'medium' | 'low';
export type TrackingPolarity = 'all' | 'positive' | 'negative';
export type TrackingDateRange =
  | 'last_7_days'
  | 'last_30_days'
  | 'this_month'
  | 'last_month'
  | 'last_3_months'
  | 'year_to_date'
  | 'custom';
export type TrackingTutorialStep = 'mentions' | 'axes' | 'index' | 'value';

export type TrackingFilterApplied =
  | { filter_name: 'source'; filter_value: TrackingCommentSource }
  | { filter_name: 'ota'; filter_value: TrackingOta }
  | { filter_name: 'intensity'; filter_value: TrackingIntensity }
  | { filter_name: 'polarity'; filter_value: TrackingPolarity }
  | { filter_name: 'topic' | 'area' | 'dimension'; filter_value: `${number}` }
  | { filter_name: 'date_range'; filter_value: TrackingDateRange }
  | { filter_name: 'hotels'; filter_value: 'all' | 'custom' }
  | { filter_name: 'group_by'; filter_value: 'all' | 'brand' | 'region' }
  | { filter_name: 'clear_all' };

export interface TrackingEventParams {
  widget_clicked: { widget_name: TrackingWidget; action: TrackingWidgetAction };
  tab_selected: { tab: TrackingScope; widget_name: 'topic_trend' | 'breakdown' };
  filter_applied: TrackingFilterApplied;
  filter_removed: { filter_name: 'topic' | 'area' | 'dimension'; filter_value: `${number}` };
  search_performed: { results_count: number };
  mention_chip_clicked: { topic_id?: number; dimension_id?: number };
  comment_source_opened: { comment_source?: TrackingCommentSource };
  comment_translated: { comment_source?: TrackingCommentSource };
  report_exported: { export_method: 'download' | 'email' };
  tutorial_viewed: Record<string, never>;
  tutorial_closed: { step: TrackingTutorialStep };
}

export type TrackingEvent = keyof TrackingEventParams;

export const TRACKING_COMMENT_SOURCE_BY_PRODUCT_ID: Partial<Record<number, TrackingCommentSource>> = {
  1: 'followup',
  2: 'onsite',
  3: 'online',
  11: 'concierge',
};

export const TRACKING_ENTRY_PRODUCT_BY_ID: Partial<Record<number, 'followup' | 'onsite' | 'online'>> = {
  1: 'followup',
  2: 'onsite',
  3: 'online',
};

export const TRACKING_OTA_BY_ID: Partial<Record<number, TrackingOta>> = {
  1: 'tripadvisor',
  2: 'booking',
  3: 'expedia',
  4: 'despegar',
  30: 'google',
  31: 'facebook',
  34: 'expedia_group',
  35: 'airbnb',
};

export const TRACKING_DATE_RANGE_BY_LABEL: Partial<Record<string, TrackingDateRange>> = {
  'filters.dates.lastSevenDays': 'last_7_days',
  'filters.dates.lastThirtyDays': 'last_30_days',
  'filters.dates.thisMonth': 'this_month',
  'filters.dates.lastMonth': 'last_month',
  'filters.dates.lastThreeMonths': 'last_3_months',
  'filters.dates.yearToDate': 'year_to_date',
  'filters.dates.custom': 'custom',
};

export const TRACKING_TAXONOMY_FILTER: Record<TrackingScope, 'topic' | 'area' | 'dimension'> = {
  topics: 'topic',
  areas: 'area',
  dimensions: 'dimension',
};

export const TRACKING_GROUP_BY: Record<'all' | 'brands' | 'regions', 'all' | 'brand' | 'region'> = {
  all: 'all',
  brands: 'brand',
  regions: 'region',
};

export const TRACKING_HIGHLIGHT_WIDGET: Record<'best' | 'worst' | 'improved', TrackingWidget> = {
  best: 'most_valued',
  worst: 'least_valued',
  improved: 'biggest_improvement',
};

export const SENTIMENT_LIST_WIDGETS = {
  trend: 'topic_trend',
  breakdown: 'breakdown',
  ranking: 'property_ranking',
} as const satisfies Record<string, TrackingListWidget>;

const CONTEXT_BY_PATH: Record<string, TrackingContext> = {
  [SENTIMENT_SUMMARY_PATH]: { product: 'sentiment', section: 'sentiment', subsection: 'summary' },
  [SENTIMENT_COMMENTS_PATH]: { product: 'sentiment', section: 'sentiment', subsection: 'comments' },
};

const CORPORATE_SECTION_BY_SEGMENT: Record<string, string> = { cases: 'desk', sentimiento: 'sentiment' };

const CORPORATE_SUBSECTION_BY_SEGMENT: Record<string, string> = {
  resumen: 'summary',
  comentarios: 'comments',
  ejecutivo: 'executive',
  propiedades: 'properties',
  tendencias: 'trends',
  cobertura: 'coverage',
};

function cleanPath(url: string): string {
  return url.split(/[?#]/)[0].replace(/\/+$/, '');
}

function corporateContextFor(url: string): TrackingContext | null {
  const [root, sectionSegment, subsectionSegment] = cleanPath(url).split('/').filter(Boolean);
  if (root !== 'intercadena' || !sectionSegment) return null;
  const context: TrackingContext = {
    product: 'corporate',
    section: CORPORATE_SECTION_BY_SEGMENT[sectionSegment] ?? sectionSegment,
  };
  if (subsectionSegment) {
    context.subsection = CORPORATE_SUBSECTION_BY_SEGMENT[subsectionSegment] ?? subsectionSegment;
  }
  return context;
}

export function routeContextFor(url: string): TrackingContext | null {
  const path = cleanPath(url);
  return CONTEXT_BY_PATH[path] ?? corporateContextFor(path);
}

export function trackingContextFor(url: string): Partial<TrackingContext> {
  return routeContextFor(url) ?? {};
}
